import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { normalizeApiError } from './error.utils';
import { SKIP_ERROR_TOAST, SKIP_AUTH_LOGOUT } from './http-context-tokens';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service';
import { AuthService } from '../services/authentication/auth-service';
import { ConfirmService } from '../services/confirm.service';
import { TranslocoService } from '@jsverse/transloco';
import * as Sentry from '@sentry/angular';

const LOGIN_ENDPOINT = '/api/users/login';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);
  const authService = inject(AuthService);
  const confirmService = inject(ConfirmService);
  const router = inject(Router);
  const transloco = inject(TranslocoService);

  return next(req).pipe(
    catchError((error: unknown) => {
      const apiError = normalizeApiError(error, {
        offline: transloco.translate('common.networkError'),
        unexpected: transloco.translate('common.unexpectedError'),
      });

      // HMRC routes (`/api/companies/hmrc/...` and `/api/accountants/.../hmrc/...`) surface a
      // 401 when the HMRC *grant* expired (CONTRACT §D: HMRC_NEEDS_REAUTH) — this is NOT an app
      // session expiry, so it must never log the user out. The HMRC page handles it by showing
      // a reconnect button.
      const isHmrcRequest = req.url.includes('/hmrc/');

      // The AI CSV import proxy (`/bank-feed/import-csv-ai[/accept]`) returns a 401 when the
      // downstream JestyLine JWT is missing/expired — this is NOT the app session expiring, so
      // it must never log the user out. The import modal surfaces the error inline instead.
      const isCsvAiRequest = req.url.includes('/bank-feed/import-csv-ai');

      if (
        apiError.status === 401 &&
        !req.context.get(SKIP_AUTH_LOGOUT) &&
        !req.url.includes(LOGIN_ENDPOINT) &&
        !isHmrcRequest &&
        !isCsvAiRequest
      ) {
        authService.logout();
        void router.navigateByUrl('/login');
        return throwError(() => apiError);
      }

      const sentryError = new Error(`HTTP ${apiError.status} - ${apiError.message}`);
      Sentry.captureException(sentryError, {
        extra: {
          url: req.url,
          method: req.method,
          status: apiError.status,
          code: apiError.code,
          fieldErrors: apiError.fieldErrors,
          responseBody: error instanceof HttpErrorResponse ? error.error : undefined,
        },
      });

      if (apiError.code === 'EFATURA_SERVICE_UNAVAILABLE') {
        void confirmService.showAlert({
          title: 'e-Fatura Servisi Kullanılamıyor',
          description:
            'e-Fatura Entegratör servisine şu anda ulaşılamıyor. Bakım çalışması veya geçici bir kesinti nedeniyle e-Belge gönderme/alma işlemleri yapılamamaktadır. Diğer işlemlerinize devam edebilirsiniz. Lütfen birkaç dakika sonra tekrar deneyiniz.',
          alert: {
            severity: 'warning',
            title: 'Geçici kesinti',
            description: 'Bu tür kesintiler genellikle kısa sürer. Birkaç dakika sonra tekrar deneyebilirsiniz.',
          },
          severity: 'danger',
        });
        return throwError(() => apiError);
      }

      // HMRC 401s are grant re-auth prompts handled inline by the HMRC dashboard (reconnect
      // button); don't fire the generic error toast — several VAT reads 401 in parallel and
      // would otherwise spam. Non-401 HMRC errors still toast normally.
      if (!req.context.get(SKIP_ERROR_TOAST) && !(isHmrcRequest && apiError.status === 401)) {
        toastService.error(transloco.translate('common.error'), apiError.message);
      }

      return throwError(() => apiError);
    }),
  );
};
